import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import _ from 'lodash';
import styled from 'styled-components';
import Paper from '@material-ui/core/Paper';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import {
  MdEdit, MdDelete, MdClose, MdFileDownload,
} from 'react-icons/lib/md';

import { API_BASE_URL } from '../../styles/constant';
import { getBudgetTracks } from '../../services/budgetTrackServices';
import blockProps from '../../utils/blockProps';
import AppShell from '../../components/AppShell';
import Auth from '../../components/Auth';
import Loader from '../../components/Loader';
import DataTable from '../../components/DataTable';
import ModalForm from '../../components/ModalForm';
import CitySelector from '../../components/CitySelector';
import ModalConfirmation from '../../components/ModalConfirmation';
import SessionVisualization from '../../components/SessionVisualization';

class BudgetTrack extends React.Component {
  static propTypes = {
    authenticatedUserInfo: PropTypes.object.isRequired,
  };

  state = {
    dataLoaded: false,
    budgetTracks: [],
    headers: [],
    error: null,
    tabValue: 0,
    jypCity:
      this.props.authenticatedUserInfo.type === 'ADMIN'
        ? 'OVERALL'
        : this.props.authenticatedUserInfo.location || '',
    loading: false,
    snackbarOpened: false,
    snackbarMessage: '',
    serverError: false,
  };

  dataTableRef = null;

  componentDidMount() {
    this.unsubscribe = getBudgetTracks((budgetTracks) => {
      this.setState({ budgetTracks });
    });

    this.fetchDatatableHeaders();
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  fetchDatatableHeaders = async () => {
    try {
      const {
        data: { data },
      } = await axios.options(`${API_BASE_URL}/datatable/budget-track`);
      this.setState({ dataLoaded: true, headers: data || [] });
    } catch (error) {
      this.setState({ dataLoaded: true, error: error.message });
    }
  };

  getFilteredBudgetTrack = () => {
    if (this.state.jypCity === 'OVERALL') {
      return this.state.budgetTracks;
    }
    return _.filter(this.state.budgetTracks, { jypCity: this.state.jypCity });
  };

  deleteBudget = async (budgetId, callback) => {
    this.setState({ loading: true });
    try {
      const { data } = await axios.delete(`${API_BASE_URL}/budgetTrack/remove/${budgetId}`);
      const response = data.data || {};

      this.setState(
        {
          loading: false,
          snackbarOpened: true,
          snackbarMessage: response.message,
        },
        () => {
          if (this.dataTableRef) {
            this.dataTableRef.resetSelectedRows();
          }
          callback();
        },
      );
    } catch (error) {
      const errorMessages = _.get(error, 'response.data.error');
      this.setState({
        loading: false,
        serverError: true,
        snackbarOpened: true,
        snackbarMessage: errorMessages || 'something went wrong',
      });
    }
  };

  handleSnackbarClose = () => {
    this.setState({
      snackbarOpened: false,
      snackbarMessage: '',
      serverError: false,
    });
  };

  renderToolbars = (selectedRows) => {
    if (selectedRows.length === 0) {
      return (
        <ToolbarContainer>
          {this.props.authenticatedUserInfo.type === 'ADMIN' && (
            <CitySelector
              initialValue={this.state.jypCity}
              onChange={(city) => {
                this.setState({ jypCity: city });
              }}
              disabled={this.props.authenticatedUserInfo.type !== 'ADMIN'}
              addOverall
            />
          )}

          <ModalForm
            key="create-budget"
            tooltipTitle="Create Budget"
            trigger={(
              <Button variant="contained" color="primary">
                Create Budget
              </Button>
)}
            apiEndpoint={`${API_BASE_URL}/budgetTrack/create/?userId=${
              this.props.authenticatedUserInfo.id
            }`}
          />

          {this.props.authenticatedUserInfo.type === 'ADMIN' && (
            <Tooltip title="Download CSV">
              <IconButton
                aria-label="Download CSV"
                onClick={() => {
                  const downloadUrl = `${API_BASE_URL}/budgetTrack/downloadCsv/?city=${
                    this.state.jypCity
                  }`;
                  window.open(downloadUrl, '_blank');
                }}
              >
                <MdFileDownload />
              </IconButton>
            </Tooltip>
          )}
        </ToolbarContainer>
      );
    }

    return (
      <ToolbarContainer>
        {selectedRows.length === 1 && [
          <ModalForm
            key="edit-budget"
            tooltipTitle="Edit Budget"
            trigger={(
              <IconButton aria-label="Edit Budget">
                <MdEdit size={24} />
              </IconButton>
)}
            apiEndpoint={`${API_BASE_URL}/budgetTrack/update/?id=${selectedRows[0]}&userType=${
              this.props.authenticatedUserInfo.type
            }`}
            method="put"
            onClose={() => {
              if (this.dataTableRef) {
                this.dataTableRef.resetSelectedRows();
              }
            }}
          />,
          <ModalConfirmation
            key="delete-budget"
            trigger={(
              <IconButton aria-label="Delete">
                <MdDelete />
              </IconButton>
)}
            onConfirm={({ closeModal }) => {
              this.deleteBudget(selectedRows[0], closeModal);
            }}
          />,
        ]}
      </ToolbarContainer>
    );
  };

  render() {
    return (
      <div>
        <AppShell>
          {this.state.error ? (
            <Typography variant="subheading" color="error">
              {this.state.error}
            </Typography>
          ) : !this.state.dataLoaded ? (
            <Loader />
          ) : (
            <div>
              <TabsContainer>
                <Tabs
                  value={this.state.tabValue}
                  onChange={(event, tabValue) => {
                    this.setState({ tabValue });
                  }}
                >
                  <Tab label="Budgets" />
                  <Tab label="Visualization" />
                </Tabs>
              </TabsContainer>

              {this.state.tabValue === 0 && (
                <DataTable
                  ref={(comp) => {
                    this.dataTableRef = comp;
                  }}
                  title="Budget Track"
                  tableHeaders={this.state.headers}
                  tableData={this.getFilteredBudgetTrack()}
                  renderToolbars={selectedRows => this.renderToolbars(selectedRows)}
                />
              )}
              {this.state.tabValue === 1 && (
                <SessionVisualization apiEndPoint={`${API_BASE_URL}/budgetTrack/dashboard`} />
              )}
            </div>
          )}
        </AppShell>

        <Snackbar
          open={this.state.snackbarOpened}
          autoHideDuration={3000}
          onClose={this.handleSnackbarClose}
        >
          <StyledSnackbarContent
            serverError={this.state.serverError}
            message={this.state.snackbarMessage}
            action={[
              <IconButton
                key="close"
                aria-label="Close"
                color="inherit"
                onClick={this.handleSnackbarClose}
              >
                <MdClose size={20} />
              </IconButton>,
            ]}
          />
        </Snackbar>

        {this.state.loading && <Loader />}
      </div>
    );
  }
}

export default props => <Auth>{({ authState }) => <BudgetTrack {...props} {...authState} />}</Auth>;

const TabsContainer = styled(Paper).attrs({
  elevation: 4,
})`
  margin-bottom: 16px;

  && {
    background: ${props => props.theme.palette.primary.main};
  }

  & * {
    color: ${props => props.theme.palette.primary.contrastText} !important;
  }
`;

const ToolbarContainer = styled.div`
  display: flex;
  align-items: center;

  & > * + * {
    margin-left: 10px !important;
  }
`;

const StyledSnackbarContent = styled(blockProps(SnackbarContent, ['serverError']))`
  background-color: ${props => (props.serverError ? 'red' : 'green')} !important;
`;



// WEBPACK FOOTER //
// ./src/pages/BudgetTrack/BudgetTrack.js